import { GEBAEUDETYP_LABEL, MERKMAL_LABEL } from '../../components/planungsphase/fragenkatalog';
import { stabileSerialisierung, stabilerInhaltsHash } from '../ablageHilfen';
import type { AusgabeUnterlage, Fassungsentscheidung, UnterlagenAusgabe } from '../unterlagenAusgabenService';
import { steckbriefAuszug, type Folgeunterlage, type ProjektSteckbrief } from './steckbrief';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AENDERUNGSDIENST — Inhaltsvergleich, nicht Zeitstempel
 * (docs/planungsphase-automatisierung-plan.md, Stufe 6.1)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bei Freigabe oder Versand einer Folgeunterlage wird der Steckbrief-Auszug
 * eingefroren (`projekt_unterlagen_ausgaben.auszug`). Aendert sich spaeter die
 * Planungsphase oder ein Projektdatum, vergleicht diese Datei den
 * eingefrorenen Auszug mit dem heutigen und benennt die Abweichungen so, dass
 * ein Koordinator sie im Dialog lesen kann („Merkmal Kran: nein → ja").
 *
 * ═══ WAS ALS GLEICH GILT ══════════════════════════════════════════════════
 *   - Leerraum: „Stadt  Dortmund " = „Stadt Dortmund".
 *   - Datumsformat: „04.05.2026" = „2026-05-04" = „2026-05-04T00:00:00Z".
 *   - Nichts: `null`, `undefined` und der leere Text sind dasselbe Nichts;
 *     ein Objekt, dessen Felder alle leer sind, ist ebenfalls Nichts.
 *   - Listen mit `code` oder `id` werden ueber diesen Schluessel verglichen,
 *     nicht ueber die Position — eine neue Frage in der Mitte verschiebt sonst
 *     alle folgenden und meldete zehn Abweichungen statt einer.
 *   - Listen aus Werten (Anhang-II-Nummern) gelten als EINE Angabe.
 *   - Eine Frage, die nur auf EINER Seite steht und dort offen und ohne
 *     Antwort ist, ist keine Abweichung: sie traegt keinen Inhalt, den die
 *     Unterlage haette uebernehmen koennen. Sonst meldete eine frisch
 *     ausgewertete Baubeschreibung 141 Zeilen „Frage …: — → Status: offen"
 *     (SiGe-Plan FehMare, 29.10.2026). Unterlagen der Checkliste
 *     zaehlen weiter — dort ist ein neuer offener Eintrag eine echte Aenderung.
 *
 * ═══ HASH = KENNUNG DER AENDERUNG ═════════════════════════════════════════
 * `abweichungsHash` haengt nur an Pfad und (normalisiertem) Alt-/Neu-Wert —
 * nicht an Beschriftungen oder Klartexten, die sich mit einer Textkorrektur
 * aendern duerfen. Dieselbe Aenderung ergibt denselben Hash; so findet die
 * Entscheidung „nicht erheblich" genau die Aenderung wieder, fuer die sie
 * getroffen wurde, und eine weitere Aenderung fragt neu (Plan 6.1).
 *
 * ═══ ZWEI WERTEMENGEN FUER DIESELBEN UNTERLAGEN ═══════════════════════════
 * Der Steckbrief sagt `ufsa` und `bauordnung` (`Folgeunterlage`), die
 * Ablage und die Ausgabentabelle sagen `unterlage` und `baustellenordnung`
 * (`ErzeugendesModul`, CHECK in Nr. 163). `FOLGEUNTERLAGE_JE_AUSGABE` ist die
 * eine Bruecke; `steckbriefAuszugFuerAusgabe` nutzt sie.
 *
 * ═══ KEIN DOM, KEINE SUPABASE ═════════════════════════════════════════════
 * Pur und in der Node-Umgebung testbar. Laden und Schreiben kennt
 * `unterlagenAusgabenService.ts`, den Hook `useAbweichungen.ts`.
 */

// ═══════════════════════════════════════════════════════════════════════════
// UNTERLAGEN — Bruecke zwischen Steckbrief und Ausgabentabelle
// ═══════════════════════════════════════════════════════════════════════════

/** Reihenfolge, in der Dialog und Reiter die Unterlagen zeigen. */
export const AUSGABE_UNTERLAGEN: readonly AusgabeUnterlage[] =
    ['vorankuendigung', 'sigeplan', 'asplan', 'unterlage', 'baustellenordnung', 'handbuch'];

export const FOLGEUNTERLAGE_JE_AUSGABE: Record<AusgabeUnterlage, Folgeunterlage> = {
    vorankuendigung: 'vorankuendigung',
    sigeplan: 'sigeplan',
    asplan: 'asplan',
    unterlage: 'ufsa',
    baustellenordnung: 'bauordnung',
    handbuch: 'handbuch',
};

export const AUSGABE_UNTERLAGE_LABEL: Record<AusgabeUnterlage, string> = {
    vorankuendigung: 'Vorankündigung',
    sigeplan: 'SiGe-Plan',
    asplan: 'A+S-Plan',
    unterlage: 'Unterlage für spätere Arbeiten',
    baustellenordnung: 'Baustellenordnung/HSE',
    handbuch: 'SiGe-Handbuch',
};

export const istAusgabeUnterlage = (v: unknown): v is AusgabeUnterlage =>
    typeof v === 'string' && (AUSGABE_UNTERLAGEN as readonly string[]).includes(v);

/** Der heutige Steckbrief-Auszug fuer eine Unterlage der Ausgabentabelle. */
export const steckbriefAuszugFuerAusgabe = (sb: ProjektSteckbrief, unterlage: AusgabeUnterlage): Record<string, unknown> =>
    steckbriefAuszug(sb, FOLGEUNTERLAGE_JE_AUSGABE[unterlage]);

// ═══════════════════════════════════════════════════════════════════════════
// ABWEICHUNG
// ═══════════════════════════════════════════════════════════════════════════

export interface Abweichung {
    /** Pfad im Auszug: `umfang.arbeitstage`, `merkmale.kran`, `fragen[PL-C-11].status`. */
    pfad: string;
    /** Fuer Menschen: „Merkmal Kran", „Ausführungszeitraum Beginn", „Frage PL-C-11 Status". */
    label: string;
    /** Normalisierter alter Wert (Leerraum, Datumsformat), `null` = nichts. */
    alt: unknown;
    /** Normalisierter neuer Wert, `null` = nichts. */
    neu: unknown;
    /** „nein", „04.05.2026", „2.400", „—" fuer nichts. */
    altText: string;
    neuText: string;
}

// ─── Normalisieren ──────────────────────────────────────────────────────────

const istObjekt = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * Ein Tagesdatum als ISO-Tag — aus ISO (auch mit Zeitanteil) oder
 * TT.MM.JJJJ. Reines Zerlegen, kein `Date`: das verschoebe den Tag je
 * Zeitzone. Alles andere bleibt, was es ist (`null`).
 */
const alsIsoTag = (s: string): string | null => {
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ].*)?$/);
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    const de = s.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (de) return `${de[3]}-${de[2].padStart(2, '0')}-${de[1].padStart(2, '0')}`;
    return null;
};

/** Leerraum zusammenziehen, Datum vereinheitlichen; leer → `null`. */
const normalisiereText = (s: string): string | null => {
    const t = s.replace(/\s+/g, ' ').trim();
    if (!t) return null;
    return alsIsoTag(t) ?? t;
};

/**
 * Vergleichsform eines Wertes: Texte getrimmt, Daten als ISO-Tag, Schluessel
 * sortiert, `undefined` → `null`. Listen behalten ihre Reihenfolge — die hat
 * der Auszug festgelegt (Code, Id, Nummer).
 */
export const normalisiere = (v: unknown): unknown => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'string') return normalisiereText(v);
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'boolean') return v;
    if (Array.isArray(v)) return v.map(normalisiere);
    if (istObjekt(v)) {
        const aus: Record<string, unknown> = {};
        Object.keys(v).sort().forEach(k => {
            if (v[k] !== undefined) aus[k] = normalisiere(v[k]);
        });
        return aus;
    }
    return null;
};

/** Nichts — auch ein Objekt oder eine Liste, in der nichts steht. */
const istLeer = (v: unknown): boolean => {
    if (v === null || v === undefined) return true;
    if (typeof v === 'string') return v.trim() === '';
    if (Array.isArray(v)) return v.every(istLeer);
    if (istObjekt(v)) return Object.values(v).every(istLeer);
    return false;
};

/** Inhaltlich gleich — auf den normalisierten Werten. */
const gleich = (a: unknown, b: unknown): boolean =>
    (istLeer(a) && istLeer(b)) || stabileSerialisierung(a) === stabileSerialisierung(b);

// ─── Listen mit Schluessel ──────────────────────────────────────────────────

const LISTEN_SCHLUESSEL: readonly string[] = ['code', 'id'];

/**
 * Der Schluessel, ueber den zwei Listen elementweise vergleichbar sind:
 * alle Elemente beider Listen sind Objekte mit demselben Textfeld `code`
 * oder `id`, und die Werte sind je Liste eindeutig. Sonst `null`.
 */
const listenSchluessel = (a: unknown[], b: unknown[]): string | null => {
    const alle = [...a, ...b];
    if (!alle.length || !alle.every(istObjekt)) return null;
    for (const schl of LISTEN_SCHLUESSEL) {
        const passt = (liste: unknown[]): boolean => {
            const werte = liste.map(e => (e as Record<string, unknown>)[schl]);
            if (!werte.every(w => typeof w === 'string' && w.trim())) return false;
            return new Set(werte).size === werte.length;
        };
        if (passt(a) && passt(b)) return schl;
    }
    return null;
};

const nachSchluessel = (liste: unknown[], schl: string): Map<string, Record<string, unknown>> => {
    const m = new Map<string, Record<string, unknown>>();
    liste.forEach(e => {
        const o = e as Record<string, unknown>;
        m.set(String(o[schl]), o);
    });
    return m;
};

const sortiertNumerisch = (a: string, b: string): number => a.localeCompare(b, 'de', { numeric: true });

/** Eine (normalisierte) Frage ohne Inhalt: Status offen oder leer, keine Antwort. */
const istOffeneFrage = (o: Record<string, unknown> | undefined): boolean =>
    !!o && (istLeer(o.status) || o.status === 'offen') && istLeer(o.antwort);

export interface AbweichungsOptionen {
    /**
     * Auch Fragen melden, die nur auf einer Seite stehen und dort offen und
     * ohne Antwort sind. Nur fuer den Rueckfall auf Entscheide, deren Hash
     * vor dem 29.10.2026 MIT diesen Zeilen gerechnet wurde (`abweichungsStaende`).
     */
    offeneFragenMelden?: boolean;
}

// ─── Beschriftungen ─────────────────────────────────────────────────────────

/** Beschriftung je Pfad des Auszugs — Rueckfall auf den Pfad (`auszugLabel`). */
export const AUSZUG_LABEL: Record<string, string> = {
    'projekt.nummer': 'Projektnummer',
    'projekt.name': 'Projektname',
    'projekt.adresse': 'Projektadresse',
    'bauherr.name': 'Bauherr Name',
    'bauherr.anschrift': 'Bauherr Anschrift',
    'bauherr.telefon': 'Bauherr Telefon',
    'bauherr.email': 'Bauherr E-Mail',
    'dritter.name': 'Dritter nach § 4 Name',
    'dritter.anschrift': 'Dritter nach § 4 Anschrift',
    'dritter.rolle': 'Dritter nach § 4 Rolle',
    dritter: 'Dritter nach § 4',
    baubeschreibung: 'Baubeschreibung',
    bauaufgabe: 'Bauaufgabe',
    'ausfuehrung.beginn': 'Ausführungszeitraum Beginn',
    'ausfuehrung.ende': 'Ausführungszeitraum Ende',
    'umfang.arbeitstage': 'Arbeitstage',
    'umfang.maxBeschaeftigte': 'Höchstzahl Beschäftigte',
    'umfang.personentage': 'Personentage',
    'umfang.mehrereArbeitgeber': 'Mehrere Arbeitgeber',
    'umfang.unternehmerOhneBeschaeftigte': 'Unternehmer ohne Beschäftigte',
    gebaeudetyp: 'Gebäudetyp',
    'anhang2.nummern': 'Anhang II Nr.',
    'anhang2.offen': 'Anhang II ungeklärt',
    'anhang2.vorankuendigungIds': 'Anhang II (Vorankündigung Punkt 11)',
    'pflichten.vorankuendigung': 'Pflicht Vorankündigung',
    'pflichten.sigeplan': 'Pflicht SiGe-Plan',
    'pflichten.koordinator': 'Pflicht Koordinator',
    'pflichten.ufsa': 'Pflicht Unterlage für spätere Arbeiten',
    'pflichten.asplan': 'Pflicht A+S-Plan',
    fragen: 'Fragen',
    unterlagen: 'Unterlagen',
    merkmale: 'Merkmale',
};

/** Kurzform der Merkmale fuer „Merkmal …" — die Maskentexte (`MERKMAL_LABEL`) sind fuer den Dialog zu lang. */
const MERKMAL_KURZ: Record<string, string> = {
    bestand: 'Bauen im Bestand',
    bestandBaujahr: 'Baujahr Bestand',
    abbruch: 'Abbruch',
    erdarbeiten: 'Erdarbeiten',
    altlastenverdacht: 'Altlastenverdacht',
    grabentiefeM: 'Grabentiefe (m)',
    absturzhoeheM: 'Absturzhöhe (m)',
    strassenbaustelle: 'Straßenbaustelle',
    betriebLaeuftWeiter: 'Betrieb läuft weiter',
    fertigteilmontage: 'Fertigteilmontage',
    kran: 'Kran',
    geruest: 'Gerüst',
    ortbeton: 'Ortbeton',
    dacharbeiten: 'Dacharbeiten',
    freileitung: 'Freileitung',
    gewaesser: 'Gewässer',
    tunnelbau: 'Tunnelbau',
    taucharbeiten: 'Taucharbeiten',
    druckluft: 'Druckluft',
    sprengarbeiten: 'Sprengarbeiten',
    strahlung: 'Strahlung',
    gefahrstoffeAnhang2: 'Gefahrstoffe Anhang II Nr. 2',
    tga: 'TGA',
    nachtarbeit: 'Nachtarbeit',
    winterbau: 'Winterbau',
    gebaeudetyp: 'Gebäudetyp',
};

/** Felder der Listenelemente (Fragen, Unterlagen). */
const FELD_LABEL: Record<string, string> = {
    status: 'Status',
    antwort: 'Antwort',
    name: 'Name',
    dokument_id: 'Beleg',
    code: 'Code',
    id: 'Id',
};

/** Benennung eines Listenelements: „Frage PL-C-11", „Unterlage „Baugrundgutachten"". */
const LISTEN_LABEL: Record<string, string> = {
    fragen: 'Frage',
    unterlagen: 'Unterlage',
};

export const auszugLabel = (pfad: string): string => {
    const direkt = AUSZUG_LABEL[pfad];
    if (direkt) return direkt;
    const merkmal = pfad.match(/^merkmale\.(\w+)$/);
    if (merkmal) return `Merkmal ${MERKMAL_KURZ[merkmal[1]] || MERKMAL_LABEL[merkmal[1]] || merkmal[1]}`;
    const element = pfad.match(/^(\w+)\[([^\]]+)\](?:\.(\w+))?$/);
    if (element) {
        const kopf = `${LISTEN_LABEL[element[1]] || element[1]} ${element[2]}`;
        return element[3] ? `${kopf} ${FELD_LABEL[element[3]] || element[3]}` : kopf;
    }
    return pfad;
};

/**
 * Beschriftung eines Listenelements aus seinem Inhalt: Unterlagen tragen
 * einen `name`, Fragen ihren `code` — der Name sagt dem Leser mehr als die
 * technische Id (`baugrundgutachten`).
 */
const elementLabel = (liste: string, schluessel: string, element: Record<string, unknown> | undefined): string => {
    const kopf = LISTEN_LABEL[liste] || liste;
    const name = element && typeof element.name === 'string' ? element.name.trim() : '';
    return name && name !== schluessel ? `${kopf} „${name}"` : `${kopf} ${schluessel}`;
};

// ─── Klartexte ──────────────────────────────────────────────────────────────

const PFLICHT_TEXT: Record<string, string> = { pflicht: 'Pflicht', keine: 'keine Pflicht', ungeklaert: 'ungeklärt' };

const STATUS_TEXT: Record<string, string> = {
    offen: 'offen',
    gestellt: 'gestellt',
    beantwortet: 'beantwortet',
    teilbeantwortet: 'teilweise beantwortet',
    nicht_beantwortet: 'nicht beantwortet',
    gegenstandslos: 'gegenstandslos',
    angefordert: 'angefordert',
    liegt_vor: 'liegt vor',
    nicht_erforderlich: 'nicht erforderlich',
};

/** ISO-Tag → TT.MM.JJJJ durch Zerlegen. */
const tagDeutsch = (iso: string): string => {
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

/** Enum-Werte nur dort uebersetzen, wo der Pfad sie ausweist — ein Projektname „keine" bleibt „keine". */
const textWert = (pfad: string, s: string): string => {
    if (pfad.startsWith('pflichten.')) return PFLICHT_TEXT[s] || s;
    if (pfad === 'gebaeudetyp') return (GEBAEUDETYP_LABEL as Record<string, string>)[s] || s;
    if (/\.status$/.test(pfad)) return STATUS_TEXT[s] || s;
    return tagDeutsch(s);
};

/**
 * Felder, die bei einem ganzen Listenelement NICHT in den Klartext gehoeren:
 * Code bzw. Id stehen schon in der Beschriftung („Frage PL-C-02"), und bei
 * einem Element mit Id oder Code gilt das auch fuer den Namen („Unterlage
 * „Baugrundgutachten""). Ein Objekt ohne Kennung (der Dritte) behaelt ihn.
 */
const imLabel = (o: Record<string, unknown>, k: string): boolean =>
    k === 'id' || k === 'code' || (k === 'name' && ('id' in o || 'code' in o));

/** Der Klartext eines (normalisierten) Wertes fuer den Dialog. */
export const wertText = (pfad: string, v: unknown): string => {
    if (istLeer(v)) return '—';
    if (typeof v === 'boolean') return v ? 'ja' : 'nein';
    if (typeof v === 'number') return v.toLocaleString('de-DE');
    if (typeof v === 'string') return textWert(pfad, v);
    if (Array.isArray(v)) return v.filter(x => !istLeer(x)).map(x => wertText(pfad, x)).join(', ') || '—';
    if (istObjekt(v)) {
        return Object.keys(v)
            .filter(k => !istLeer(v[k]) && !imLabel(v, k))
            .map(k => `${FELD_LABEL[k] || k}: ${wertText(`${pfad}.${k}`, v[k])}`)
            .join('; ') || '—';
    }
    return String(v);
};

// ─── Vergleich ──────────────────────────────────────────────────────────────

const abweichung = (pfad: string, label: string, alt: unknown, neu: unknown): Abweichung => ({
    pfad,
    label,
    alt: istLeer(alt) ? null : alt,
    neu: istLeer(neu) ? null : neu,
    altText: wertText(pfad, alt),
    neuText: wertText(pfad, neu),
});

/**
 * Rekursiver Vergleich zweier NORMALISIERTER Werte. `label` ist gesetzt,
 * solange wir uns in einem Listenelement bewegen (dann heisst das Feld
 * „Frage PL-C-11 Status" statt `fragen[PL-C-11].status`).
 */
const vergleiche = (
    pfad: string,
    alt: unknown,
    neu: unknown,
    aus: Abweichung[],
    label: string | null,
    optionen: AbweichungsOptionen,
): void => {
    if (istLeer(alt) && istLeer(neu)) return;

    // Objekt oder Liste gegen Nichts: die leere Seite als leeres Objekt bzw.
    // leere Liste lesen, damit der Vergleich feldweise bleibt — „Dritter nach
    // § 4 Name: — → GU Bau GmbH" statt eines Klumpens unter `dritter`.
    if (istObjekt(alt) && istLeer(neu)) neu = {};
    else if (istObjekt(neu) && istLeer(alt)) alt = {};
    else if (Array.isArray(alt) && istLeer(neu)) neu = [];
    else if (Array.isArray(neu) && istLeer(alt)) alt = [];

    if (istObjekt(alt) && istObjekt(neu)) {
        const schluessel = [...new Set([...Object.keys(alt), ...Object.keys(neu)])].sort(sortiertNumerisch);
        schluessel.forEach(k => {
            const kindPfad = pfad ? `${pfad}.${k}` : k;
            const kindLabel = label ? `${label} ${FELD_LABEL[k] || k}` : null;
            vergleiche(kindPfad, alt[k], neu[k], aus, kindLabel, optionen);
        });
        return;
    }

    if (Array.isArray(alt) && Array.isArray(neu)) {
        const schl = listenSchluessel(alt, neu);
        if (schl) {
            const a = nachSchluessel(alt, schl);
            const n = nachSchluessel(neu, schl);
            const liste = pfad.split('.').pop() || pfad;
            [...new Set([...a.keys(), ...n.keys()])].sort(sortiertNumerisch).forEach(k => {
                const element = n.get(k) ?? a.get(k);
                const elementPfad = `${pfad}[${k}]`;
                const bezeichnung = elementLabel(liste, k, element);
                if (!a.has(k) || !n.has(k)) {
                    // Eine offene Frage ohne Antwort, die hinzukommt oder entfaellt,
                    // traegt keinen Inhalt (Dateikopf, „Was als gleich gilt").
                    if (liste === 'fragen' && !optionen.offeneFragenMelden && istOffeneFrage(element)) return;
                    // Hinzugekommen oder entfallen: EINE Abweichung je Element —
                    // „Frage PL-C-02: — → Status: beantwortet; Antwort: …", nicht ein Eintrag je Feld.
                    aus.push(abweichung(elementPfad, bezeichnung, a.get(k), n.get(k)));
                    return;
                }
                vergleiche(elementPfad, a.get(k), n.get(k), aus, bezeichnung, optionen);
            });
            return;
        }
        if (!gleich(alt, neu)) aus.push(abweichung(pfad, label || auszugLabel(pfad), alt, neu));
        return;
    }

    if (!gleich(alt, neu)) aus.push(abweichung(pfad, label || auszugLabel(pfad), alt, neu));
};

/**
 * Alle Abweichungen zwischen dem eingefrorenen Auszug (`alt`) und dem
 * heutigen (`neu`) — rekursiv, in stabiler Reihenfolge (Schluessel sortiert),
 * Leerraum und Datumsformat gleichgesetzt. Leer, wenn inhaltlich gleich.
 */
export const abweichungen = (
    alt: Record<string, unknown>,
    neu: Record<string, unknown>,
    optionen: AbweichungsOptionen = {},
): Abweichung[] => {
    const aus: Abweichung[] = [];
    vergleiche('', normalisiere(alt || {}), normalisiere(neu || {}), aus, null, optionen);
    return aus;
};

// ═══════════════════════════════════════════════════════════════════════════
// HASHES
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kennung EINER Aenderung: nur Pfad, Alt und Neu — keine Beschriftungen.
 * Zwei gleiche Listen ergeben denselben Hash, auch wenn die Dialogtexte
 * inzwischen anders lauten.
 */
export const abweichungsHash = (liste: readonly Abweichung[]): string =>
    stabilerInhaltsHash(
        [...liste]
            .sort((a, b) => sortiertNumerisch(a.pfad, b.pfad))
            .map(a => ({ pfad: a.pfad, alt: a.alt, neu: a.neu })),
    );

/** Kennung eines Auszugs — fuer `projekt_unterlagen_ausgaben.auszug_hash`. */
export const auszugHash = (auszug: Record<string, unknown>): string =>
    stabilerInhaltsHash(normalisiere(auszug || {}));

// ═══════════════════════════════════════════════════════════════════════════
// STAND JE UNTERLAGE
// ═══════════════════════════════════════════════════════════════════════════

export interface AbweichungsStand {
    unterlage: AusgabeUnterlage;
    /** Die letzte freigegebene oder versendete Ausgabe — der Anker. */
    ausgabe: UnterlagenAusgabe;
    abweichungen: Abweichung[];
    /** `abweichungsHash(abweichungen)` — auch fuer die leere Liste. */
    hash: string;
    /**
     * Die juengste Entscheidung zu GENAU dieser Ausgabe und diesem Hash (oder
     * dem Hash mit offenen Fragen, Rueckfall in `abweichungsStaende`), sonst `null`.
     */
    entscheidung: Fassungsentscheidung | null;
    /**
     * Es gibt Abweichungen UND niemand hat sie entschieden (oder nur
     * „später" gesagt). `neue_fassung` und `nicht_erheblich` schliessen die
     * Frage fuer diese Aenderung; eine weitere Aenderung ergibt einen neuen
     * Hash und oeffnet sie wieder.
     */
    offen: boolean;
}

const zeit = (iso: string | null | undefined): number => {
    const t = Date.parse(String(iso || ''));
    return Number.isFinite(t) ? t : 0;
};

/** Die juengste Entscheidung zu (Ausgabe, einer der Hashes) — bei gleicher Zeit gewinnt die zuerst gelieferte. */
const letzteEntscheidung = (
    entscheidungen: readonly Fassungsentscheidung[],
    ausgabeId: string,
    hashes: readonly string[],
): Fassungsentscheidung | null => {
    let beste: Fassungsentscheidung | null = null;
    entscheidungen.forEach(e => {
        if (e.ausgabe_id !== ausgabeId || !hashes.includes(e.abweichung_hash)) return;
        if (!beste || zeit(e.entschieden_am) > zeit(beste.entschieden_am)) beste = e;
    });
    return beste;
};

/**
 * Je Unterlage mit letzter Ausgabe: Abweichungen zwischen eingefrorenem
 * Auszug und heutigem Steckbrief, dazu die Entscheidung dazu und ob die
 * Frage offen ist. Unterlagen ohne Ausgabe (nur Entwurf, nie freigegeben)
 * fehlen — dort gibt es keine geltende Fassung, die veralten koennte.
 */
export const abweichungsStaende = (
    ausgaben: Partial<Record<AusgabeUnterlage, UnterlagenAusgabe>>,
    entscheidungen: readonly Fassungsentscheidung[],
    steckbrief: ProjektSteckbrief,
): AbweichungsStand[] => {
    const staende: AbweichungsStand[] = [];
    AUSGABE_UNTERLAGEN.forEach(unterlage => {
        const ausgabe = ausgaben[unterlage];
        if (!ausgabe) return;
        const alt = ausgabe.auszug || {};
        const neu = steckbriefAuszugFuerAusgabe(steckbrief, unterlage);
        const liste = abweichungen(alt, neu);
        const hash = abweichungsHash(liste);
        // Bis 29.10.2026 hing der Hash auch an neuen offenen Fragen. Ein Entscheid
        // zu dieser groesseren Liste deckt die kleinere mit ab — ohne Rueckfall
        // fragte das Update jeden schon entschiedenen Stand noch einmal. Es
        // zaehlt der juengste Entscheid zu einem der beiden Hashes.
        const hashMitOffenenFragen = abweichungsHash(abweichungen(alt, neu, { offeneFragenMelden: true }));
        const entscheidung = letzteEntscheidung(entscheidungen || [], ausgabe.id, [hash, hashMitOffenenFragen]);
        staende.push({
            unterlage,
            ausgabe,
            abweichungen: liste,
            hash,
            entscheidung,
            offen: liste.length > 0 && (!entscheidung || entscheidung.entscheid === 'spaeter'),
        });
    });
    return staende;
};
